'use client';

import type { PostPaginationProps } from '@routeroyal/theme-core';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysPostPaginationProps extends PostPaginationProps {
  messages?: Record<string, unknown>;
}

/**
 * AllysPostPagination - Allys-themed post pagination
 * Page navigation with Allys styling
 */
export function AllysPostPagination({
  currentPage,
  totalPages,
  onPageChange,
  messages,
  className = '',
}: AllysPostPaginationProps) {
  const postsMsg = messages?.posts as Record<string, unknown> | undefined;
  const paginationMsg = postsMsg?.pagination as Record<string, unknown> | undefined;
  if (totalPages <= 1) {
    return null;
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className={`flex items-center justify-center gap-2 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}
      aria-label="Pagination"
    >
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`
          flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
          transition-all duration-300
          ${
            currentPage <= 1
              ? 'text-gray-300 cursor-not-allowed bg-gray-50'
              : 'text-[#243462] hover:bg-[#243462] hover:text-white bg-gray-100'
          }
        `}
        aria-label="Previous page"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="hidden sm:inline">{safeGetString(paginationMsg?.previous, 'Previous')}</span>
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((page, index) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="px-3 py-2 text-gray-400 font-medium"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`
                w-11 h-11 rounded-xl text-sm font-bold
                transition-all duration-300
                ${
                  currentPage === page
                    ? 'bg-[#243462] text-white shadow-lg'
                    : 'text-[#243462] hover:bg-[#BBA339]/10 bg-gray-100'
                }
              `}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )
        )}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`
          flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
          transition-all duration-300
          ${
            currentPage >= totalPages
              ? 'text-gray-300 cursor-not-allowed bg-gray-50'
              : 'text-[#243462] hover:bg-[#243462] hover:text-white bg-gray-100'
          }
        `}
        aria-label="Next page"
      >
        <span className="hidden sm:inline">{safeGetString(paginationMsg?.next, 'Next')}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </nav>
  );
}
